import { useEffect, useState } from 'react';

import Accordion from './Accordion';
import LayoutContainer from './LayoutContainer';

import icBall from '@/assets/ic_ball_blue.svg';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { AccordionItem, FaqSectionProps } from '@/types/faq';

const TeamBuildingAndAwards = ({ state, title }: FaqSectionProps) => {
  const { isMobile } = useIsDesktop();
  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);

  useEffect(() => {
    if (state?.accordionId) {
      setActiveAccordion(state.accordionId);
    }
  }, [state?.accordionId]);

  // Manejador para alternar acordeones
  const toggleAccordion = (id: string) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  useEffect(() => {
    if (activeAccordion) {
      const element = document.getElementById(activeAccordion);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [activeAccordion]); // Only re-run when state.accordionId changes

  // Datos para los acordeones
  const accordionItems: AccordionItem[] = [
    {
      id: 'juego-que-es',
      title: '¿Qué es Gran DT? ¿Cómo se juega?',
      content: (
        <p>
          Gran DT es una competencia entre equipos imaginarios creados por los
          participantes, que se convierten en verdaderos directores técnicos.
          Para jugar, tenés que armar un equipo, ponerle un nombre, elegir a los
          titulares y al capitán, hacer cambios y transferencias.
          <br />
          <br />
          El objetivo del juego es acumular la mayor cantidad de puntos en base
          a la actuación real de los futbolistas en el torneo de Primera
          División y así convertirse en el ganador de la fecha de Gran DT o en
          el ganador del Torneo general. Estos puntos estarán dados por la
          calificación que reciben los jugadores en el Diario Clarín después de
          cada partido, los cuales varían de acuerdo a las incidencias de los
          partidos, estableciendo puntos a favor o puntos en contra durante el
          desarrollo del Juego.
          <br />
          <br />
          Dentro del juego, <strong>hay dos equipos diferentes</strong>, que
          podés armar y dirigir:
          <br />
          <br />
          <ul className="list-disc pl-6 marker:text-black">
            <li>
              <strong>Un equipo de Primera División</strong>, con 11 titulares y
              4 suplentes. Tenés 9 tácticas disponibles para armar tu equipo y
              un presupuesto imaginario de $65.000.000.
            </li>
            <li>
              <strong>Un equipo de Fútbol 5</strong>, formado por un arquero y
              cuatro futbolistas de Primera División, sin límite de presupuesto.
            </li>
          </ul>
        </p>
      ),
    },
    {
      id: 'juego-como-armo-equipo',
      title: '¿Cómo armo mi equipo?',
      content: (
        <>
          <p>
            Cada equipo estará integrado por{' '}
            <strong>
              15 futbolistas de la Primera División del Fútbol argentino
            </strong>{' '}
            (11 titulares y 4 suplentes). De los titulares, se deberá designar
            un capitán para poder confirmar el equipo. No hay límite de
            jugadores elegidos por club y existe un presupuesto imaginario de
            $65.000.000 para adquirir a los jugadores.
          </p>
          <br />
          <p>
            Hay 9 tácticas disponibles, para armar tu equipo. Deberás elegir una
            para comenzar y podrás cambiarla en el transcurso del juego. Las
            tácticas posibles son:{' '}
            <strong>
              4-4-2, 4-3-3, 3-4-3, 4-5-1, 3-5-2, 5-3-2, 3-3-4, 4-2-4, 5-2-3.
            </strong>
          </p>
        </>
      ),
    },
    {
      id: 'juego-como-selecciono-capitan',
      title: '¿Cómo selecciono a mi capitán?',
      content: (
        <>
          <p>
            En el momento de armar el equipo, se deberá elegir a uno de los 11
            titulares para que sea el capitán del equipo. El capitán duplicará
            la calificación que le otorga Clarín en cada fecha
            (&ldquo;Calificación Clarín&rdquo; del 1 al 10), sin tener en cuenta
            las incidencias del partido.
          </p>
          <br />
          <p>
            El capitán se elige desde la columna <strong>`CAP`</strong>,
            seleccionando el futbolista que querés que duplique la calificación
            Clarín.
          </p>
          <br />
          <p>
            Podés modificar tu capitán todas las veces que quieras. Ingresá a{' '}
            <strong>Mi Equipo</strong> y hacé click en el botón{' '}
            <strong>Cambiar capitán</strong> o bien desde{' '}
            <strong>Transferencias</strong>, seleccionando el jugador desde la
            desde la columna <strong>`CAP`</strong>.
          </p>
        </>
      ),
    },
    {
      id: 'juego-como-realizo-cambios',
      title: '¿Cómo realizo cambios y transferencias en mi equipo?',
      content: (
        <p>
          Los cambios son la sustitución de un titular de tu equipo por el
          suplente de la misma posición, que está incluido en tu equipo. Son
          ilimitados y gratuitos y pueden realizarse antes y después del período
          de veda.
          <br />
          <br />
          Los cambios se pueden realizar desde dos pantallas. Por un lado, se
          pueden hacer desde <strong>Mi Equipo</strong>. Para eso, hay que hacer
          click en el botón <strong>Cambiar titular por suplente</strong> y
          elegir al titular que se quiere sacar. El suplente pasará
          automáticamente a ser titular en el equipo. Por otro lado, también se
          pueden realizar los cambios desde la pantalla{' '}
          <strong>Transferencias</strong>. Allí, primero se debe elegir al
          suplente que se quiere ingresar y luego seleccionar al titular que
          deseás sacar de tu equipo.
          <br />
          <br />
          Una transferencia es cuando un futbolista incluido en tu equipo se
          reemplaza por otro que no está entre tus 15 elegidos (11 titulares y 4
          suplentes). Son ilimitadas, la primera es gratuita y para las
          siguientes necesitás ingresar un PIN.
          <br />
          <br />
          Las transferencias se realizan desde la pantalla Transferencias. Para
          hacerlas, primero debés seleccionar al jugador saliente y luego
          seleccionar al futbolista elegido para que ingrese en tu equipo.
          <br />
          <br />
          Es importante que las transferencias respeten las reglas básicas del
          juego:
          <br />
          <ol className="list-decimal pl-6 mt-4 marker:text-blue-700 marker:font-bold">
            <li className="mb-2">
              Una vez hecha la transferencia, el equipo debe quedar armado de
              tal modo que respete cualquiera de las nueve tácticas disponibles.
            </li>
            <li className="mb-2">
              No se podrá superar el presupuesto máximo estipulado.
            </li>
            <li className="mb-2">
              Si el futbolista que se reemplaza es el capitán del equipo, su
              condición será trasladada al jugador que ingrese en su lugar, es
              decir que el reemplazante será desde ese momento el capitán.
            </li>
          </ol>
        </p>
      ),
    },
    {
      id: 'juego-como-cambio-tactica',
      title: '¿Cómo cambio de táctica?',
      content: (
        <p>
          Hay 9 tácticas disponibles en Gran DT: 4-4-2, 4-3-3, 3-4-3, 4-5-1,
          3-5-2, 5-3-2, 3-3-4, 4-2-4 y 5-2-3. Para cambiar de táctica primero
          debés seleccionar el jugador de tu equipo que querés que salga y luego
          seleccionar un futbolista del listado de jugadores disponibles, que
          sea de otra posición.
          <br />
          <br />
          Por ejemplo, si tenés la táctica 4-4-2 pero querés cambiar a 4-3-3,
          debés seleccionar a un volante de tu equipo y que en su lugar ingrese
          un delantero.
        </p>
      ),
    },
    {
      id: 'juego-que-es-veda',
      title: '¿Qué es la veda y cuándo empieza?',
      content: (
        <p>
          La veda es el período de tiempo que transcurre desde un minuto antes
          del comienzo del primer partido de la fecha hasta una vez finalizado
          el último partido de la fecha. Durante ese tiempo no podrán realizarse
          cambios ni transferencias en el equipo, no se podrá cambiar al
          capitán, y algunas funciones del juego estarán desactivadas.
        </p>
      ),
    },
    {
      id: 'juego-cuales-son-los-premios',
      title: '¿Cuáles son los premios?',
      content: (
        <p>
          En esta oportunidad el ganador de cada una de las fechas del juego
          recibirá <strong>$1.500.000</strong> en caso de que sea usuario
          Premium. Caso contrario, se llevará <strong>$100.000</strong>.
          Mientras que el campeón de Gran DT, al finalizar el Torneo, ganará{' '}
          <strong>$8.500.000</strong> si es Premium y <strong>$500.000</strong>{' '}
          si no lo es.
          <br />
          Para conocer los premios de Fútbol 5 y otros torneos especiales,
          consultá el Reglamento del juego.
        </p>
      ),
    },
    {
      id: 'juego-como-sumo-medallas',
      title: '¿Cómo sumo medallas?',
      content: (
        <div className="text-sm">
          <p>
            El Medallero premia tu rendimiento en todas las activaciones del
            juego. A mejor rendimiento, tendrás mayor cantidad de medallas. Hay
            un Ranking Medallero, donde se puede ver a los participantes que
            tienen la mayor cantidad de medallas.
          </p>
          <p className="mt-4">
            Los participantes logran sumar medallas en función de su desempeño
            en el juego. Suman medallas de acuerdo al siguiente esquema:
          </p>

          {/* Gran DT Torneo Primera División */}
          <div className="mt-6">
            <h3 className="font-bold text-base mb-2">
              Gran DT Torneo Primera División
            </h3>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-1/2 overflow-hidden">
                <table className="w-full bg-gray-100 rounded-md overflow-hidden">
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="p-3 text-blue-700 font-medium">
                        Top 75.000 de la fecha
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        1 medalla
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="p-3 text-blue-700 font-medium">
                        Top 25.000 de la fecha
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        2 medallas
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="p-3 text-blue-700 font-medium">
                        Top 10.000 de la fecha
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        3 medallas
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 text-blue-700 font-medium">
                        Top 100 de la fecha
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        5 medallas
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="w-full md:w-1/2 overflow-hidden">
                <table className="w-full bg-gray-100 rounded-md overflow-hidden">
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="p-3 text-blue-700 font-medium">
                        Top 75.000 del torneo
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        4 medallas
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="p-3 text-blue-700 font-medium">
                        Top 25.000 del torneo
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        8 medallas
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="p-3 text-blue-700 font-medium">
                        Top 10.000 del torneo
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        12 medallas
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 text-blue-700 font-medium">
                        Top 100 del torneo
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        18 medallas
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Gran DT Fútbol 5 */}
          <div className="mt-6">
            <h3 className="font-bold text-base mb-2">Gran DT Fútbol 5</h3>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-1/2 overflow-hidden">
                <table className="w-full bg-gray-100 rounded-md overflow-hidden">
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="p-3 text-blue-700 font-medium">
                        Top 25.000 de la fecha
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        1 medalla
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="p-3 text-blue-700 font-medium">
                        Top 5.000 de la fecha
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        3 medallas
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 text-blue-700 font-medium">
                        Top 100 de la fecha
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        5 medallas
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="w-full md:w-1/2 overflow-hidden">
                <table className="w-full bg-gray-100 rounded-md overflow-hidden">
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="p-3 text-blue-700 font-medium">
                        Top 25.000 del torneo
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        5 medallas
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="p-3 text-blue-700 font-medium">
                        Top 5.000 del torneo
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        10 medallas
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 text-blue-700 font-medium">
                        Top 100 del torneo
                      </td>
                      <td className="p-3 text-right text-blue-700 font-medium">
                        15 medallas
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Trivia */}
          <div className="mt-6">
            <h3 className="font-bold text-base mb-2">Trivia</h3>
            <div className="w-full overflow-hidden">
              <table className="w-full bg-gray-100 rounded-md overflow-hidden">
                <tbody>
                  <tr>
                    <td className="p-3 text-blue-700 font-medium">
                      Responder 10 preguntas correctamente en 3 minutos
                    </td>
                    <td className="p-3 text-right text-blue-700 font-medium">
                      1 medalla
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Mini Ligas */}
          <div className="mt-6">
            <h3 className="font-bold text-base mb-2">Miniligas</h3>
            <div className="w-full overflow-hidden">
              <table className="w-full bg-gray-100 rounded-md overflow-hidden">
                <tbody>
                  <tr>
                    <td className="p-3 text-blue-700 font-medium">
                      Campeón de cada Miniliga
                    </td>
                    <td className="p-3 text-right text-blue-700 font-medium">
                      5 medallas (máximo 5 medallas por usuario)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Fechas Premium */}
          <div className="mt-6">
            <h3 className="font-bold text-base mb-2">Fechas Premium</h3>
            <div className="w-full overflow-hidden">
              <table className="w-full bg-gray-100 rounded-md overflow-hidden">
                <tbody>
                  <tr>
                    <td className="p-3 text-blue-700 font-medium">Top 1.000</td>
                    <td className="p-3 text-right text-blue-700 font-medium">
                      10 medallas
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Desafíos 1 a 1 */}
          <div className="mt-6">
            <h3 className="font-bold text-base mb-2">Desafíos 1 a 1</h3>
            <div className="w-full overflow-hidden">
              <table className="w-full bg-gray-100 rounded-md overflow-hidden">
                <tbody>
                  <tr>
                    <td className="p-3 text-blue-700 font-medium">
                      Ganador de 15 desafíos
                    </td>
                    <td className="p-3 text-right text-blue-700 font-medium">
                      1 medalla (máximo 5 medallas por usuario)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <LayoutContainer>
      <div className="flex gap-x-3 items-center content-center mb-8">
        {isMobile && (
          <img
            alt="Gran DT - logo calendario"
            className="w-6 h-6"
            src={icBall}
          />
        )}
        <span className={`text-lg text-[#333333] font-bold`}>{title}</span>
      </div>

      {accordionItems.map((item) => (
        <Accordion
          key={item.id}
          id={item.id}
          isOpen={activeAccordion === item.id}
          title={item.title}
          onToggle={() => toggleAccordion(item.id)}
        >
          <div className="text-sm text-gray-600">{item.content}</div>
        </Accordion>
      ))}
    </LayoutContainer>
  );
};

export default TeamBuildingAndAwards;
